import {
    Alert,
    ModalFooter,
    Stack,
    Text,
    ThemeColorVariable,
    ThemeFontSizeVariable,
    ThemeSpaceVariable,
} from "@caspeco/casper-ui-library";
import { useAvailableTimes } from "api/api-hooks/use-available-times";
import { useExternalBookingSettings } from "api/api-hooks/use-external-booking-settings";
import { BookableItem } from "components/booking-modal/bookable-item";
import ContinueButton from "components/selection-components/customize-wizard/continue-button";
import PreorderContinueButton from "components/selection-components/preorder/preorder-continue-button";
import { useBookingStateContext } from "hooks/use-booking-state";
import { useDurationFormatter } from "hooks/use-duration-formatter";
import { useTranslation } from "react-i18next";
import { AvailabilityState, getAvailabilityState, isTextRowsOnly } from "utils/availability-utils";
import { TimeSlot } from "types/time-slot";
import { isTimeBookable, isTimeWaitListable } from "utils/time-slot-filters";

interface BookingModalFooterProps {
    item: BookableItem | null;
    step: number;
    originalTime: TimeSlot | undefined;
    selectedTime: TimeSlot | undefined;
    selectedWaitListTimes: TimeSlot[];
    onContinue: () => Promise<void>;
    onPreorderContinue: () => Promise<void>;
    onWaitlistContinue: () => void;
    onFilterOpen: () => void;
}

function BookingModalFooter({
    item,
    step,
    originalTime,
    selectedTime,
    selectedWaitListTimes,
    onContinue,
    onPreorderContinue,
    onWaitlistContinue,
    onFilterOpen,
}: BookingModalFooterProps) {
    const { t } = useTranslation();
    const { date, guests, isBookingRequest } = useBookingStateContext();
    const { isLoading: isLoadingAvailableTimes } = useAvailableTimes(date);
    const formatter = useDurationFormatter();
    const { data: settings } = useExternalBookingSettings();

    const displaySection = item?.activities[0] ?? null;
    const hasGuestsAndDate = !!guests && !!date;
    const shouldRemoveActivity = !!(originalTime && !selectedTime);
    const availabilityState = getAvailabilityState(displaySection?.timeSets ?? [], guests ?? 0);
    const hasWaitListOnlySlots =
        !isBookingRequest &&
        !!guests &&
        !!item?.supportsWaitlist &&
        !!displaySection?.timeSets.some((timeSet) =>
            timeSet.times.some(
                (time) => isTimeWaitListable(time, guests) && !isTimeBookable(time, guests)
            )
        );

    const finalName = item?.name ?? "";

    const duration = () => {
        if (!selectedTime) return "";

        if (item?.chains) {
            const chain = item.chains.find((c) => c[0].start.isSame(selectedTime.start));
            if (chain) {
                const lastIndex = chain.length - 1;
                const lastSlot = chain[lastIndex];
                const lastRecoup =
                    item.activities[lastIndex]?.timeSets.find((ts) => ts.id === lastSlot.ruleId)
                        ?.recoupTime ?? 0;
                const syntheticEnd = lastSlot.end.clone().subtract(lastRecoup, "minutes");
                return formatter(selectedTime.start, syntheticEnd);
            }
        }

        const recoupTime =
            displaySection?.timeSets.find((ts) => ts.id === selectedTime.ruleId)?.recoupTime || 0;
        return formatter(
            selectedTime.start,
            selectedTime.end.clone().subtract(recoupTime, "minute")
        );
    };

    const continueButtonText = () => {
        if (selectedWaitListTimes.length === 1) {
            return t("waitList.continueWithSelectedWaitListTime", {
                count: selectedWaitListTimes.length,
            });
        }
        if (selectedWaitListTimes.length > 1) {
            return t("waitList.continueWithSelectedWaitListTimes", {
                count: selectedWaitListTimes.length,
            });
        }
        if (shouldRemoveActivity) return t("removeActivityX", { activityName: finalName });
        if (!selectedTime) return t("addWithEllipsis");
        const timeText = t("continueWithTimeX", { time: selectedTime.start.format("HH:mm") });
        return settings?.hideEndTimeForGuest ? timeText : `${timeText} (${duration()})`;
    };

    const isContinueDisabled = item?.supportsWaitlist
        ? !selectedTime && !originalTime && selectedWaitListTimes.length === 0
        : !selectedTime && !shouldRemoveActivity;

    const waitListFooterGuidance = () => {
        if (
            step !== 0 ||
            !hasWaitListOnlySlots ||
            selectedTime ||
            shouldRemoveActivity ||
            selectedWaitListTimes.length === 0
        ) {
            return null;
        }

        return t("waitList.waitListFooterAddMore");
    };

    const waitListGuidance = waitListFooterGuidance();

    const baseContinueButton =
        step === 0 ? (
            <ContinueButton
                isDisabled={isContinueDisabled}
                buttonText={continueButtonText()}
                onContinue={
                    item?.supportsWaitlist && !selectedTime && !shouldRemoveActivity
                        ? onWaitlistContinue
                        : onContinue
                }
                trackingId={item?.continueTrackingId ?? ""}
            />
        ) : (
            <PreorderContinueButton
                onContinue={onPreorderContinue}
                trackingId="continue-select-activity-preorder"
            />
        );

    const continueButton = waitListGuidance ? (
        <Stack w="full" spacing={ThemeSpaceVariable.Small}>
            <Text
                color={ThemeColorVariable.OnBackgroundSubdued}
                fontSize={ThemeFontSizeVariable.Small}
                textAlign="center"
            >
                {waitListGuidance}
            </Text>
            {baseContinueButton}
        </Stack>
    ) : (
        baseContinueButton
    );

    const footerContent = () => {
        // Nothing to book whatever guests and date are chosen, so no button to go and choose them.
        if (item?.isInformationOnly) return null;
        if (isLoadingAvailableTimes) return continueButton;
        if (!hasGuestsAndDate) {
            return (
                <ContinueButton
                    buttonText={t("experienceView.selectGuestsAndDate")}
                    onContinue={onFilterOpen}
                    trackingId={item?.filterButtonTrackingId ?? ""}
                />
            );
        }
        // There is nothing to add to a booking here, so the only way on is the request flow.
        if (item?.isRequestOnly && item.onRequestContinue) {
            return (
                <ContinueButton
                    buttonText={t("bookingRequest.continueToRequest")}
                    onContinue={item.onRequestContinue}
                    trackingId="continue-to-request-from-experience"
                />
            );
        }
        if (isBookingRequest || item?.isRequestOnly) return continueButton;
        // Text rows carry a message instead of times: nothing to add, and nothing fully booked.
        if (isTextRowsOnly(displaySection?.timeSets ?? [])) return null;
        if (availabilityState === AvailabilityState.FullyBooked) {
            return (
                <Alert type="info">
                    {t("activityFullyBooked", { activityName: finalName, guests })}
                </Alert>
            );
        }
        if (availabilityState === AvailabilityState.UnavailableForGroupSize) {
            return (
                <Alert type="info">
                    {t("activityUnavailableTodayGroupSize", {
                        activityName: finalName,
                        guests,
                    })}
                </Alert>
            );
        }
        return continueButton;
    };

    return (
        <ModalFooter
            position="absolute"
            bottom={0}
            left={0}
            right={0}
            p={ThemeSpaceVariable.Medium}
            bg={waitListGuidance ? ThemeColorVariable.Surface : "transparent"}
            boxShadow="none"
        >
            {footerContent()}
        </ModalFooter>
    );
}

export { BookingModalFooter };
